// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
// Import the centralized mapping for consistent icons
import { getLineItemServiceType } from '../../../../constants/lineItemServiceTypes';
import { addTravelDays } from '../../../../utils/travelDate';
import { getLineItemImageUrl } from '../../../../utils/resourcePathHelper';
import { imageSize } from '../../../../constants/files';
import PropTypes from 'prop-types';
import TravelerAppLineItemDisplay from '../../../../components/LineItem/Display/TravelerAppLineItemDisplay';
import ImageCarousel from '../../../../components/Itineraries/templates/Trips/CherryOutput/ImageCarousel/ImageCarousel';
import { SignOut, X } from '@phosphor-icons/react';
import {
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton
} from '@mui/material';
import * as P from '@phosphor-icons/react';
import './LineItemCard.css';

const LineItemCard = ({
  lineitem = {},
  rawstartdate = null,
  daynumber = null,
  isCheckout = false
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isDesktop = useMediaQuery('(min-width:1024px)');
  const scrollYBeforeOpenRef = useRef(0);

  const {
    servicetypecode = '',
    enddayoffset = 0,
    lineitemimage = [],
    title = ''
  } = lineitem;

  // Calculate start and end dates if in itinerary context
  let startDate = null;
  let endDate = null;

  if (rawstartdate && daynumber) {
    startDate = addTravelDays(rawstartdate, parseInt(daynumber, 10) - 1);
    const daysToAdd = enddayoffset || 0;

    if (servicetypecode === 'HTL') {
      endDate = addTravelDays(startDate, parseInt(daysToAdd, 10));
    } else if (daysToAdd > 0) {
      endDate = addTravelDays(startDate, parseInt(daysToAdd, 10));
    } else {
      endDate = startDate;
    }
  }

  // Build image URLs - thumbnails for card, full-size for modal carousel
  const thumbnailUrls = lineitemimage
    .sort((a, b) => a.seq - b.seq)
    .map((img) => {
      if (img.resourcepath) {
        return getLineItemImageUrl({
          resourcePath: img.resourcepath,
          sizePrefix: imageSize.thumbnail.prefix
        });
      }
      return null;
    })
    .filter(Boolean);

  const fullSizeUrls = lineitemimage
    .sort((a, b) => a.seq - b.seq)
    .map((img) => {
      if (img.resourcepath) {
        return getLineItemImageUrl({
          resourcePath: img.resourcepath,
          sizePrefix: imageSize.medium.prefix
        });
      }
      return null;
    })
    .filter(Boolean);

  const hasImages = thumbnailUrls.length > 0;

  // Get service type icon using the centralized mapping
  const getServiceTypeIcon = () => {
    const mapped = getLineItemServiceType(servicetypecode);
    const iconref = mapped?.iconref;
    const ServiceTypeIcon =
      iconref && typeof P[iconref] === 'object' ? P[iconref] : null;
    return ServiceTypeIcon;
  };

  const ServiceTypeIcon = getServiceTypeIcon();

  // Augment lineitem with calculated fields
  const augmentedLineItem = {
    ...lineitem,
    ...(startDate && { startDate }),
    ...(endDate && { endDate })
  };

  const handleCardClick = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (!isDialogOpen) return;

    const body = document.body;
    scrollYBeforeOpenRef.current = window.scrollY || 0;

    const previousStyles = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollYBeforeOpenRef.current}px`;
    body.style.width = '100%';

    return () => {
      body.style.position = previousStyles.position;
      body.style.top = previousStyles.top;
      body.style.width = previousStyles.width;

      window.scrollTo(0, scrollYBeforeOpenRef.current);
    };
  }, [isDialogOpen]);

  return (
    <>
      <div
        className={`activity-card ${isCheckout ? 'activity-card-checkout' : ''}`}
        onClick={isCheckout ? null : handleCardClick}
        style={{ cursor: isCheckout ? 'default' : 'pointer' }}
      >
        <div className='activity-card__wrapper'>
          {/* Left Section - Icon */}
          <div className='activity-card__left'>
            {isCheckout ? (
              <div className='activity-card-checkout__icon'>
                <SignOut size={20} />
              </div>
            ) : (
              <div className='activity-card__icon'>
                {ServiceTypeIcon && <ServiceTypeIcon size={20} />}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className='activity-card__content'>
            <TravelerAppLineItemDisplay
              lineItem={augmentedLineItem}
              isCheckout={isCheckout}
              isPreview={true}
              isDesktop={isDesktop}
            />
          </div>
        </div>

        {/* Images Section */}
        {hasImages && !isCheckout && (
          <div className='activity-card__images-section'>
            {thumbnailUrls.map((imageUrl, index) => (
              <div className='activity-card__image-primary' key={index}>
                <img
                  src={imageUrl}
                  alt={`Image ${index + 1}`}
                  className='activity-card__image'
                  draggable={false}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Service Details Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth='md'
        fullWidth
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
            margin: '16px'
          }
        }}
      >
        {/* Dialog Header */}
        <DialogTitle sx={{ p: 0, position: 'relative' }}>
          <Box
            sx={{
              p: 3,
              pb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
              gap: '.75rem'
            }}
          >
            <Box
              component='h2'
              sx={{
                m: 0,
                flex: 1,
                pr: 6
              }}
            >
              {title || ''}
            </Box>
            <IconButton
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: '#333',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,1)'
                }
              }}
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent className='activity-card__dialog-content'>
          {/* Images Gallery */}
          {hasImages && (
            <div className='activity-card__dialog-images'>
              <ImageCarousel images={fullSizeUrls} />
            </div>
          )}

          {/* Service Details */}
          <Box sx={{ mb: 3 }}>
            <TravelerAppLineItemDisplay
              lineItem={augmentedLineItem}
              isCheckout={isCheckout}
              isPreview={false}
              isDesktop={isDesktop}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

LineItemCard.propTypes = {
  lineitem: PropTypes.shape({
    lineitemid: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    vendorname: PropTypes.string,
    vendorphone: PropTypes.string,
    vendorlink: PropTypes.string,
    startlocation: PropTypes.string,
    endlocation: PropTypes.string,
    starttime: PropTypes.string,
    endtime: PropTypes.string,
    durationtime: PropTypes.string,
    confirmationnumber: PropTypes.string,
    serviceclass: PropTypes.string,
    servicenumber: PropTypes.string,
    mealsincluded: PropTypes.string,
    contactname: PropTypes.string,
    servicetypecode: PropTypes.string,
    enddayoffset: PropTypes.number,
    lineitemimage: PropTypes.array,
    lineitemattachment: PropTypes.array
  }).isRequired,
  rawstartdate: PropTypes.string,
  daynumber: PropTypes.number,
  isCheckout: PropTypes.bool
};

export default LineItemCard;
